import { useFormik } from "formik";
import { createPortal } from "react-dom";
import * as Yup from "yup";
import { PortalFooter } from "../../../../../components/molecules/Footer";
import { PortalHeader } from "../../../../../components/molecules/Header";
import { FormControlInput } from "../../../../../components/organisms/Form/control";
import { ModalOverlay, ModalT1 } from "../../../../../components/organisms/Modal";
import { ModalBodyT1 } from "../../../../../components/organisms/Modal/Body";
import { ManageGeneralFormProvider } from "../../../../../components/organisms/Provider/ManageGeneralFormProvider";
import { useNotification } from "../../../../../hooks/useNotification";
import { useCreateOrderlessManufacturingEntryMutation } from "../../../../../store/apiSlice/productionApiSlice";

const rootModals = document.getElementById("root-modals");

const validationSchema = Yup.object()
  .shape({
    production_qty: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? undefined : value,
      )
      .typeError("Production quantity must be a valid number")
      .required("Production quantity is required")
      .positive("Production quantity must be greater than 0"),
    sellable_qty: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? undefined : value,
      )
      .typeError("Sellable quantity must be a valid number")
      .min(0, "Sellable quantity cannot be negative")
      .nullable(),
    on_hold_qty: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? undefined : value,
      )
      .typeError("On-hold quantity must be a valid number")
      .min(0, "On-hold quantity cannot be negative")
      .nullable(),
    rejected_qty: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? undefined : value,
      )
      .typeError("Rejected quantity must be a valid number")
      .min(0, "Rejected quantity cannot be negative")
      .nullable(),
    batch_number: Yup.string().nullable(),
    serial_number: Yup.string().nullable(),
  })
  .test(
    "bucket-sum-matches-production-qty",
    "Sum of sellable, on-hold, and rejected quantities must equal production quantity",
    function (values) {
      const { production_qty, sellable_qty, on_hold_qty, rejected_qty } = values || {};

      const hasSellable =
        sellable_qty !== "" && sellable_qty !== undefined && sellable_qty !== null;
      const hasOnHold = on_hold_qty !== "" && on_hold_qty !== undefined && on_hold_qty !== null;
      const hasRejected =
        rejected_qty !== "" && rejected_qty !== undefined && rejected_qty !== null;

      const hasAnyBucket = hasSellable || hasOnHold || hasRejected;

      if (hasAnyBucket && production_qty) {
        const sQty = Number(sellable_qty) || 0;
        const hQty = Number(on_hold_qty) || 0;
        const rQty = Number(rejected_qty) || 0;
        const pQty = Number(production_qty) || 0;

        const sum = Number((sQty + hQty + rQty).toFixed(6));
        const target = Number(pQty.toFixed(6));

        if (sum !== target) {
          return this.createError({
            path: "sellable_qty",
            message: `Bucket sum (${sum}) must equal production quantity (${target}).`,
          });
        }
      }
      return true;
    },
  );

export const OrderlessManufacturingEntryModal = ({ bomId, closeModal, onSuccess }) => {
  const notify = useNotification();
  const [createOrderlessEntry, { isLoading }] = useCreateOrderlessManufacturingEntryMutation();

  const formik = useFormik({
    initialValues: {
      production_qty: "",
      sellable_qty: "",
      on_hold_qty: "",
      rejected_qty: "",
      batch_number: "",
      serial_number: "",
    },
    validationSchema,
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async values => {
      const payload = {
        bom_id: Number(bomId),
        production_qty: values.production_qty || 0,
      };

      if (values.batch_number?.trim()) {
        payload.batch_number = values.batch_number.trim();
      }

      if (values.serial_number?.trim()) {
        payload.serial_number = values.serial_number.trim();
      }

      const hasSellable =
        values.sellable_qty !== "" &&
        values.sellable_qty !== null &&
        values.sellable_qty !== undefined;
      const hasOnHold =
        values.on_hold_qty !== "" &&
        values.on_hold_qty !== null &&
        values.on_hold_qty !== undefined;
      const hasRejected =
        values.rejected_qty !== "" &&
        values.rejected_qty !== null &&
        values.rejected_qty !== undefined;

      if (hasSellable || hasOnHold || hasRejected) {
        if (hasSellable) {
          payload.sellable_qty = values.sellable_qty || 0;
        }
        if (hasOnHold) {
          payload.on_hold_qty = values.on_hold_qty || 0;
        }
        if (hasRejected) {
          payload.rejected_qty = values.rejected_qty || 0;
        }
      }

      try {
        await createOrderlessEntry(payload).unwrap();
        notify({
          type: "success",
          message: "Orderless manufacturing entry created successfully.",
        });
        formik.resetForm();
        onSuccess?.();
        closeModal();
      } catch (error) {
        const errorDetail =
          error?.data?.detail ||
          error?.data?.message ||
          (typeof error?.data === "object"
            ? Object.entries(error.data)
                .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(", ") : val}`)
                .join(" | ")
            : null) ||
          "Failed to create orderless manufacturing entry.";

        notify({
          type: "error",
          message: errorDetail,
        });
      }
    },
  });

  const modalContent = (
    <ModalOverlay aria-modal="true" role="dialog" aria-labelledby="orderless-entry-modal-title">
      <ModalT1 $minWidth={520} $maxWidth={650} onClick={e => e.stopPropagation()}>
        <PortalHeader
          title="Orderless Manufacturing Entry"
          onClose={!isLoading ? closeModal : undefined}
        />

        <ModalBodyT1 style={{ padding: "20px 24px" }}>
          <ManageGeneralFormProvider formik={formik}>
            <form onSubmit={formik.handleSubmit}>
              <div className="row g-3">
                <div className="col-12 mb-2">
                  <FormControlInput
                    name="production_qty"
                    type="number"
                    label="Production Quantity"
                    placeholder="Enter production quantity (> 0)"
                    context="general"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="col-4 mb-2">
                  <FormControlInput
                    name="sellable_qty"
                    type="number"
                    label="Sellable Qty"
                    placeholder="0"
                    context="general"
                    disabled={isLoading}
                  />
                </div>

                <div className="col-4 mb-2">
                  <FormControlInput
                    name="on_hold_qty"
                    type="number"
                    label="On-hold Qty"
                    placeholder="0"
                    context="general"
                    disabled={isLoading}
                  />
                </div>

                <div className="col-4 mb-2">
                  <FormControlInput
                    name="rejected_qty"
                    type="number"
                    label="Rejected Qty"
                    placeholder="0"
                    context="general"
                    disabled={isLoading}
                  />
                </div>

                <div className="col-6 mb-2">
                  <FormControlInput
                    name="batch_number"
                    type="text"
                    label="Batch Number"
                    placeholder="Enter batch number (optional)"
                    context="general"
                    disabled={isLoading}
                  />
                </div>

                <div className="col-6 mb-2">
                  <FormControlInput
                    name="serial_number"
                    type="text"
                    label="Serial Number"
                    placeholder="Enter serial number (optional)"
                    context="general"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </form>
          </ManageGeneralFormProvider>
        </ModalBodyT1>

        <PortalFooter
          className="for-modal"
          isDisabled={isLoading || !formik.isValid}
          onApply={() => formik.submitForm()}
          onCancel={isLoading ? undefined : closeModal}
          applyTitle="Create Entry"
          applyVariant="primary"
          isLoading={isLoading}
        />
      </ModalT1>
    </ModalOverlay>
  );

  return rootModals ? createPortal(modalContent, rootModals) : null;
};
